import {
  ThemeColors,
  TinyDropdownMenu,
  Toolbar,
} from '@octopus-do/octopus-storybook'
import {
  LinkExternal,
  NHotkey,
  NoteStickyLight,
  Paperclip,
  Stickynote,
} from '@octopus-do/octopus-storybook/icons'
import type React from 'react'
import { forwardRef, useState } from 'react'

import { MENU_OFFSET, STICKY_NOTE_ID } from '../../constants'
import { AttachFileButton } from '../AttachFileButton'
import { ExternalLinkPopover } from '../ExternalLinkPopover'

// The menu picks the tool; the control next to it runs the pick, so a tool
// that opens something (the file picker, the link popover) is still opened
// by its own control and its own gesture.
type Tool = 'attach_file' | 'external_link' | 'sticky_note'

const DEFAULT_TOOL: Tool = 'sticky_note'

const Content: React.FC = () => (
  <TinyDropdownMenu.Group>
    <TinyDropdownMenu.Button
      hotkeys={[NHotkey]}
      icon={Stickynote}
      id="sticky_note"
      label="Sticky note"
    />
    <TinyDropdownMenu.Button
      icon={Paperclip}
      id="attach_file"
      label="Attach file"
    />
    <TinyDropdownMenu.Button
      icon={LinkExternal}
      id="external_link"
      label="External link"
    />
  </TinyDropdownMenu.Group>
)

const Trigger = forwardRef<HTMLButtonElement>((_, ref) => {
  const { active, toggle } = TinyDropdownMenu.useTrigger()

  return (
    <Toolbar.ChevronButton
      active={active}
      onClick={toggle}
      ref={ref}
      tooltip="Actions"
    />
  )
})

interface Props {
  attachDisabled: boolean
  attachLocked: boolean
  attachUploading: boolean
  onUpgradeAttachments: () => void
  toolsDisabled: boolean
}

// On a phone the three editing actions share one control (figma 86174:59769).
export const ActionsMenu: React.FC<Props> = ({
  attachDisabled,
  attachLocked,
  attachUploading,
  onUpgradeAttachments,
  toolsDisabled,
}) => {
  const [tool, setTool] = useState<Tool>(DEFAULT_TOOL)

  // the menu reports Tool values as ids
  const pick = ([next]: string[]) => setTool(next as Tool)

  return (
    <>
      {tool === 'sticky_note' && (
        <Toolbar.Button
          disabled={toolsDisabled}
          hotkeys={[NHotkey]}
          icon={NoteStickyLight}
          id={STICKY_NOTE_ID}
          tooltip="Sticky note"
        />
      )}
      {tool === 'attach_file' && (
        <AttachFileButton
          disabled={attachDisabled}
          loading={attachUploading}
          locked={attachLocked}
          onUpgrade={onUpgradeAttachments}
        />
      )}
      {tool === 'external_link' && (
        <ExternalLinkPopover disabled={toolsDisabled} />
      )}
      <TinyDropdownMenu.Root
        active={[tool]}
        content={Content}
        onClick={pick}
        sideOffset={MENU_OFFSET}
        theme={ThemeColors.light}
      >
        <Trigger />
      </TinyDropdownMenu.Root>
    </>
  )
}
